/**
 * Redux slice for managing chat mode state (Ask Chat vs RAG modes like 3M Knowledge)
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import logger from '../utils/logger/logger';
import type { ChatMessage as CoreChatMessage } from '../utils/chat/chat.service';
import { DEFAULT_IMAGE_MODEL_ID, DEFAULT_REASONING_MODEL_ID, DEFAULT_STANDARD_MODEL_ID, getToolCompatibility, getToolForModelSelection, migrateModelId } from '../utils/config/modelConfig';

const FAVORITABLE_MODEL_IDS = new Set(['gpt-5.5-fast', 'gpt-5.5-reasoning', 'claude-sonnet-5.0-fast', 'claude-opus-4.8-reasoning']);
const rawStoredFavorite = localStorage.getItem('favoriteModelId');
const storedFavorite = rawStoredFavorite ? migrateModelId(rawStoredFavorite) : null;
const defaultModelId = (storedFavorite && FAVORITABLE_MODEL_IDS.has(storedFavorite))
  ? storedFavorite
  : DEFAULT_STANDARD_MODEL_ID;
// If the stored favourite is itself a reasoning model, seed lastReasoningModelId with it
// so the reasoning-entry path in setSelectedTool restores the right model instead of the
// hardcoded DEFAULT_REASONING_MODEL_ID.
const defaultLastReasoningModelId = getToolForModelSelection(defaultModelId) === 'reasoning'
  ? defaultModelId
  : DEFAULT_REASONING_MODEL_ID;

export type ChatMode = 'Ask Chat' | '3M Knowledge';
export type RagChatType = 'Everyday Insights' | 'Legal' | 'DevOps' | 'Reporting Portal' | 'TEBG' | 'PPMO' | 'Travel & Expenses' | 'ITSM' | 'JIRA' | 'HR' | 'GlobalProcurement' | 'FP&A' | 'SAP Security' | 'EHS Sustainability' | 'Wiki-App' | 'CSR' | 'GFS' | 'EHS' | 'Planning Assistant' | 'CyberQ' | 'AI@3M' | 'P360' | null;

// Maximum number of messages to keep in history (user + assistant pairs)
// 100 messages = ~50 conversation turns, which should be sufficient for context
const MAX_HISTORY_SIZE = 100;

export interface ChatModeState {
  mode: ChatMode;
  ragChatType: RagChatType; // Active RAG chat within 3M Knowledge mode
  askChatHistory: CoreChatMessage[];
  ragHistory: CoreChatMessage[];
  isStreaming: boolean; // Track if any chat mode is currently streaming
  selectedModelId: string; // Active AI model — always compatible with the current tool
  lastReasoningModelId: string; // Last model chosen while in reasoning mode, restored on re-entry
  lastImageModelId: string; // Last model chosen while in image_generation mode, restored on re-entry
  selectedTool: string | null; // Currently active tool (e.g. 'web_search', 'translation', 'data_analysis')
  pendingPrompt: { text: string; tool?: string } | null; // Prompt selected from Prompt Library awaiting consumption by ChatInput
  pendingPromptSelection: { prompt: string; tool?: string; agentType?: string } | null; // Prompt selection awaiting user confirmation (when chat is active)
  showPromptConfirmation: boolean; // Whether the prompt library confirmation dialog is visible
}

const initialState: ChatModeState = {
  mode: 'Ask Chat',
  ragChatType: null, // Start with no RAG selected (Home is active)
  askChatHistory: [],
  ragHistory: [],
  isStreaming: false,
  selectedModelId: defaultModelId,
  lastReasoningModelId: defaultLastReasoningModelId,
  lastImageModelId: DEFAULT_IMAGE_MODEL_ID,
  selectedTool: null,
  pendingPrompt: null,
  pendingPromptSelection: null,
  showPromptConfirmation: false,
};

const chatModeSlice = createSlice({
  name: 'chatMode',
  initialState,
  reducers: {
    setChatMode: (state, action: PayloadAction<ChatMode>) => {
      state.mode = action.payload;
      // Do NOT reset ragChatType when switching to Ask Chat — preserve it so
      // returning to 3M Knowledge restores the previously selected RAG.
      // ragChatType is only cleared explicitly (e.g. clicking 3M Knowledge
      // while already on 3M Knowledge dispatches setRagChatType(null) directly).
    },
    setRagChatType: (state, action: PayloadAction<RagChatType>) => {
      state.ragChatType = action.payload;
      // Automatically switch to 3M Knowledge mode when selecting any RAG chat
      if (action.payload !== null) {
        state.mode = '3M Knowledge';
      }
    },
    setPromptFromLibrary: (state, action: PayloadAction<{ text: string; tool?: string }>) => {
      state.pendingPrompt = action.payload;
    },
    clearPendingPrompt: (state) => {
      state.pendingPrompt = null;
    },
    setPendingPromptSelection: (state, action: PayloadAction<{ prompt: string; tool?: string; agentType?: string } | null>) => {
      state.pendingPromptSelection = action.payload;
    },
    clearPendingPromptSelection: (state) => {
      state.pendingPromptSelection = null;
    },
    setShowPromptConfirmation: (state, action: PayloadAction<boolean>) => {
      state.showPromptConfirmation = action.payload;
    },
    setaskChatHistory: (state, action: PayloadAction<CoreChatMessage[]>) => {
      const newHistory = action.payload;
      // If history exceeds limit, keep only the most recent messages
      if (newHistory.length > MAX_HISTORY_SIZE) {
        state.askChatHistory = newHistory.slice(newHistory.length - MAX_HISTORY_SIZE);
      } else {
        state.askChatHistory = newHistory;
      }
    },
    setRagHistory: (state, action: PayloadAction<CoreChatMessage[]>) => {
      const newHistory = action.payload;
      // If history exceeds limit, keep only the most recent messages
      if (newHistory.length > MAX_HISTORY_SIZE) {
        state.ragHistory = newHistory.slice(newHistory.length - MAX_HISTORY_SIZE);
      } else {
        state.ragHistory = newHistory;
      }
    },
    clearaskChatHistory: (state) => {
      state.askChatHistory = [];
    },
    clearRagHistory: (state) => {
      state.ragHistory = [];
    },
    setIsStreaming: (state, action: PayloadAction<boolean>) => {
      state.isStreaming = action.payload;
    },
    setSelectedModel: (state, action: PayloadAction<string>) => {
      state.selectedModelId = action.payload;
      // If the selected model is an image model, update lastImageModelId as well
      if (getToolForModelSelection(action.payload) === 'image_generation') {
        state.lastImageModelId = action.payload;
      }
      // If the selected model is a reasoning model, update lastReasoningModelId as well
      if (getToolForModelSelection(action.payload) === 'reasoning') {
        state.lastReasoningModelId = action.payload;
      }
    },
    setSelectedTool: (state, action: PayloadAction<string | null>) => {
      const previousTool = state.selectedTool;

      // No-op: tool is not actually changing — skip all model reset/restore logic.
      if (previousTool === action.payload) return;

      // If switching to regular chat (null) or to image_generation or reasoning, clear selectedTool to allow free switching
      if (
        action.payload === null ||
        action.payload === 'image_generation' ||
        action.payload === 'reasoning'
      ) {
        state.selectedTool = null;
      } else {
        state.selectedTool = action.payload;
      }

      const isEnteringReasoningFromRegularChat = previousTool === null && action.payload === 'reasoning';
      const isLeavingReasoningToRegularChat = previousTool === 'reasoning' && action.payload === null;
      const isEnteringImageFromRegularChat = previousTool === null && action.payload === 'image_generation';
      const isLeavingImageToRegularChat = previousTool === 'image_generation' && action.payload === null;

      // Switching directly between regular chat and reasoning: restore last-used
      // reasoning model (same pattern as image_generation).
      if (isEnteringReasoningFromRegularChat || isLeavingReasoningToRegularChat) {
        if (isEnteringReasoningFromRegularChat) {
          const { compatible, fallbackId } = getToolCompatibility(state.lastReasoningModelId, 'reasoning');
          state.selectedModelId = compatible ? state.lastReasoningModelId : fallbackId;
        } else {
          state.selectedModelId = DEFAULT_STANDARD_MODEL_ID;
        }
        return;
      }

      // Same reset behaviour for direct switches between regular chat and image generation.
      if (isEnteringImageFromRegularChat || isLeavingImageToRegularChat) {
        if (isEnteringImageFromRegularChat) {
          const { compatible, fallbackId } = getToolCompatibility(state.lastImageModelId, 'image_generation');
          state.selectedModelId = compatible ? state.lastImageModelId : fallbackId;
        } else {
          state.selectedModelId = DEFAULT_STANDARD_MODEL_ID;
        }
        return;
      }

      if (action.payload === 'reasoning') {
        // Re-entering reasoning from another tool restores the prior reasoning model.
        const { compatible, fallbackId } = getToolCompatibility(state.lastReasoningModelId, 'reasoning');
        state.selectedModelId = compatible ? state.lastReasoningModelId : fallbackId;
      } else if (action.payload === 'image_generation') {
        // Re-entering image generation from another tool restores the prior image model.
        const { compatible, fallbackId } = getToolCompatibility(state.lastImageModelId, 'image_generation');
        state.selectedModelId = compatible ? state.lastImageModelId : fallbackId;
      } else {
        // Leaving a tool (or switching between non-reasoning tools):
        // if we were in reasoning, remember the model choice for next time.
        if (previousTool === 'reasoning') {
          state.lastReasoningModelId = state.selectedModelId;
        }
        if (previousTool === 'image_generation') {
          state.lastImageModelId = state.selectedModelId;
        }
        // Ensure selectedModelId is compatible with the new tool (including null = regular chat).
        const { compatible, fallbackId } = getToolCompatibility(state.selectedModelId, action.payload);
        if (!compatible) {
          state.selectedModelId = fallbackId;
        }
      }
    },
    // Voice-specific actions
    addVoiceUserMessage: {
      reducer: (state, action: PayloadAction<{ id?: string }>) => {
        // Add a "transcribing" placeholder for user's voice message
        // Always use timestamp-based ID for consistent sorting
        const timestamp = Date.now();
        const messageId = `voice-user-${timestamp}`;
        const newMessage: CoreChatMessage = {
          id: messageId,
          role: 'user',
          content: 'Transcribing...',
          isVoice: true,
          isTranscribing: true,
          turnId: action.payload.id, // Store original turn ID for matching
        };
        
        const history = state.mode === 'Ask Chat' ? state.askChatHistory : state.ragHistory;
        const updatedHistory = [...history, newMessage];
        
        if (state.mode === 'Ask Chat') {
          state.askChatHistory = updatedHistory.slice(-MAX_HISTORY_SIZE);
        } else {
          state.ragHistory = updatedHistory.slice(-MAX_HISTORY_SIZE);
        }
      },
      prepare: (payload?: { id?: string }) => {
        return { payload: payload || {} };
      }
    },
    addVoiceAssistantMessage: (state) => {
      // Add an empty assistant message that will be updated with text deltas
      const newMessage: CoreChatMessage = {
        id: `voice-assistant-${Date.now()}`,
        role: 'assistant',
        content: '',
        isVoice: true,
      };
      
      const history = state.mode === 'Ask Chat' ? state.askChatHistory : state.ragHistory;
      const updatedHistory = [...history, newMessage];
      
      if (state.mode === 'Ask Chat') {
        state.askChatHistory = updatedHistory.slice(-MAX_HISTORY_SIZE);
      } else {
        state.ragHistory = updatedHistory.slice(-MAX_HISTORY_SIZE);
      }
    },
    updateLastAssistantMessage: (state, action: PayloadAction<{ textDelta: string }>) => {
      // Append text delta to the last assistant message
      const history = state.mode === 'Ask Chat' ? state.askChatHistory : state.ragHistory;
      if (history.length > 0 && history[history.length - 1].role === 'assistant') {
        const lastMessage = history[history.length - 1];
        let contentStr = '';
        if (typeof lastMessage.content === 'string') {
          contentStr = lastMessage.content;
        } else if (Array.isArray(lastMessage.content)) {
          // If content is an array, join text fields for delta appending
          contentStr = lastMessage.content.map(item => item.text || '').join(' ');
        }
        // Smart spacing: add space ONLY after web search completion (when function call was active)
        const needsSpaceAfterFunctionCall = 
          lastMessage.functionCall?.isActive === true && 
          contentStr.length > 0 && 
          !/\s$/.test(contentStr);
        lastMessage.content = contentStr + (needsSpaceAfterFunctionCall ? ' ' : '') + action.payload.textDelta;
        // Mark function call as inactive when we start receiving text deltas (function completed)
        if (lastMessage.functionCall?.isActive) {
          lastMessage.functionCall.isActive = false;
        }
      }
    },
    updateLastAssistantMessageWithFunctionCall: (state, action: PayloadAction<{ 
      functionCall?: { type: 'web_search' | 'unknown'; message: string; isActive: boolean };
      sources?: Array<{ title: string; url: string; snippet?: string }>;
    }>) => {
      // Add or update function call metadata and/or sources to the last assistant message
      const history = state.mode === 'Ask Chat' ? state.askChatHistory : state.ragHistory;
      
      if (history.length > 0 && history[history.length - 1].role === 'assistant') {
        const lastMessage = history[history.length - 1];
        
        // Update function call if provided
        if (action.payload.functionCall) {
          lastMessage.functionCall = action.payload.functionCall;
        }
        
        // Update sources if provided
        if (action.payload.sources) {
          lastMessage.sources = action.payload.sources;
        }
      }
    },
    finalizeVoiceAssistantMessage: (state, action: PayloadAction<{ audioData?: string[]; sources?: Array<{ title: string; url: string; snippet?: string }> }>) => {
      // Finalize the last assistant voice message with audio data and sources
      const history = state.mode === 'Ask Chat' ? state.askChatHistory : state.ragHistory;
      const audioData = action.payload.audioData;
      const sources = action.payload.sources;
      
      if (history.length > 0 && history[history.length - 1].role === 'assistant' && history[history.length - 1].isVoice) {
        const lastMessage = history[history.length - 1];
        
        // Add audio data if provided
        if (audioData && audioData.length > 0) {
          lastMessage.audioData = audioData;
        }
        
        // Add sources if provided
        if (sources && sources.length > 0) {
          lastMessage.sources = sources;
        }
        
        // Mark function call as inactive
        if (lastMessage.functionCall?.isActive) {
          lastMessage.functionCall.isActive = false;
        }
      }
    },
    finalizeVoiceUserMessage: (state, action: PayloadAction<{ transcript: string; audioData?: string | string[] }>) => {
      const history = state.mode === 'Ask Chat' ? state.askChatHistory : state.ragHistory;
      const transcript = action.payload.transcript?.trim() || '';
      const audioData = action.payload.audioData;

      logger.log('[chatModeSlice] finalizeVoiceUserMessage called:', {
        hasTranscript: !!transcript,
        hasAudio: !!audioData,
        historyLength: history.length,
      });

      // Find the FIRST (earliest) user voice message that's still transcribing
      const message = history.find(m => m.role === 'user' && m.isVoice && m.isTranscribing);
      
      if (message) {
        logger.log('[chatModeSlice] Found transcribing message, updating with transcript and audio');
        // If transcript is empty or only whitespace, preserve '' as the unavailable-transcript sentinel
        message.content = transcript || '';
        message.isTranscribing = false;
        if (audioData) {
          message.audioData = audioData;
        }
      } else {
        logger.warn('[chatModeSlice] No transcribing user message found to finalize');
      }
    },
    cancelTranscribingMessages: (state) => {
      // When voice mode is stopped abruptly:
      // 1. Mark all still-transcribing user messages as cancelled
      // 2. Remove any voice assistant messages with empty/null content — these are
      //    placeholder messages created when the assistant started responding but voice
      //    mode was stopped before the response was finalised. Leaving them in history
      //    causes displayChatHistory to enter the voice-merge branch, which hides
      //    subsequent translation results behind an empty/partial message.
      const history = state.mode === 'Ask Chat' ? state.askChatHistory : state.ragHistory;

      // Iterate backwards so splicing does not shift unvisited indices
      for (let i = history.length - 1; i >= 0; i--) {
        if (history[i].role === 'user' && history[i].isVoice && history[i].isTranscribing) {
          logger.log('[chatModeSlice] Cancelling transcription for message:', history[i].id);
          history[i].content = '';
          history[i].isTranscribing = false;
        }
        // Remove voice assistant messages that were never finalised (content is empty string or empty array)
        const isEmptyContent =
          history[i].role === 'assistant' && history[i].isVoice &&
          (history[i].content === '' || (Array.isArray(history[i].content) && history[i].content.length === 0));
        if (isEmptyContent) {
          logger.log('[chatModeSlice] Removing incomplete voice assistant message:', history[i].id);
          history.splice(i, 1);
        }
      }
    },
  },
});

export const { 
  setChatMode, 
  setRagChatType, 
  setPromptFromLibrary,
  clearPendingPrompt,
  setPendingPromptSelection,
  clearPendingPromptSelection,
  setShowPromptConfirmation,
  setaskChatHistory, 
  setRagHistory, 
  clearaskChatHistory, 
  clearRagHistory, 
  setIsStreaming,
  setSelectedModel,
  setSelectedTool,
  addVoiceUserMessage,
  addVoiceAssistantMessage,
  updateLastAssistantMessage,
  updateLastAssistantMessageWithFunctionCall,
  finalizeVoiceUserMessage,
  finalizeVoiceAssistantMessage,
  cancelTranscribingMessages,
} = chatModeSlice.actions;
export const chatModeSelector = (state: { chatMode: ChatModeState }) => state.chatMode;
export default chatModeSlice.reducer;
